import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { productDetails, ProductDetail } from '@/mocks/productDetails';

interface ImportProductsModalProps {
  onClose: () => void;
  onDone: (count: number) => void;
  existingIds?: number[];
}

type ImportStatus = 'idle' | 'checking' | 'ready' | 'importing' | 'done' | 'error';

interface ProductImportRow {
  product: ProductDetail;
  exists: boolean;
  selected: boolean;
}

export default function ImportProductsModal({ onClose, onDone }: ImportProductsModalProps) {
  const [status, setStatus] = useState<ImportStatus>('idle');
  const [rows, setRows] = useState<ProductImportRow[]>([]);
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [importedCount, setImportedCount] = useState(0);

  const handleCheck = async () => {
    setStatus('checking');
    // Check which products already exist in DB by name
    const { data } = await supabase
      .from('products')
      .select('name_ar');
    const existingNames = new Set((data ?? []).map((r: { name_ar: string }) => r.name_ar));

    const mapped: ProductImportRow[] = productDetails.map(p => ({
      product: p,
      exists: existingNames.has(p.nameAr),
      selected: !existingNames.has(p.nameAr), // auto-select new ones
    }));
    setRows(mapped);
    setStatus('ready');
  };

  const toggleSelect = (idx: number) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, selected: !r.selected } : r));
  };

  const toggleAll = () => {
    const allSelected = rows.filter(r => !r.exists).every(r => r.selected);
    setRows(prev => prev.map(r => r.exists ? r : { ...r, selected: !allSelected }));
  };

  const handleImport = async () => {
    const toImport = rows.filter(r => r.selected && !r.exists);
    if (toImport.length === 0) return;

    setStatus('importing');
    setProgress(0);
    let count = 0;

    for (let i = 0; i < toImport.length; i++) {
      const p = toImport[i].product;
      try {
        const { error } = await supabase.from('products').insert({
          name_ar: p.nameAr,
          category: p.category,
          badge: p.badge ?? '',
          rating: p.rating,
          reviews: p.reviews,
          price: p.price,
          original_price: p.originalPrice,
          short_desc: p.shortDesc,
          long_desc: p.longDesc,
          images: p.images,
          highlights: p.highlights,
          specs: p.specs,
          in_box: p.inBox,
          related_ids: p.relatedIds,
          updated_at: new Date().toISOString(),
        });
        if (!error) count++;
      } catch {
        // continue
      }
      setProgress(Math.round(((i + 1) / toImport.length) * 100));
    }

    setImportedCount(count);
    if (count === toImport.length) {
      setStatus('done');
    } else {
      setErrorMsg(`تم نقل ${count} من ${toImport.length} منتج — بعض المنتجات فشلت`);
      setStatus('error');
    }
    onDone(count);
  };

  const newCount = rows.filter(r => !r.exists).length;
  const selectedCount = rows.filter(r => r.selected && !r.exists).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" dir="rtl">
      <div className="bg-white rounded-2xl w-full max-w-lg flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-amber-50 rounded-xl">
              <i className="ri-database-2-line text-amber-600 text-base"></i>
            </div>
            <div>
              <h2 className="font-bold text-[#1a1a1a] text-base">نقل المنتجات إلى قاعدة البيانات</h2>
              <p className="text-xs text-gray-400 mt-0.5">{productDetails.length} منتج جاهز للنقل</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* Idle state */}
          {status === 'idle' && (
            <div className="flex flex-col items-center text-center gap-5 py-6">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-50 rounded-2xl">
                <i className="ri-upload-cloud-2-line text-amber-500 text-4xl"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1a1a1a] text-base mb-2">نقل المنتجات بضغطة واحدة</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  سيتم فحص المنتجات الموجودة في قاعدة البيانات أولاً،
                  ثم نقل المنتجات الجديدة فقط دون تكرار.
                </p>
              </div>
              <div className="w-full bg-gray-50 rounded-xl p-4 text-right">
                <p className="text-xs font-bold text-gray-500 mb-3">المنتجات التي سيتم فحصها:</p>
                <div className="flex flex-col gap-2">
                  {productDetails.map(p => (
                    <div key={p.id} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        {p.images?.[0] && (
                          <img src={p.images[0]} alt={p.nameAr} className="w-full h-full object-cover object-top" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1a1a1a] truncate">{p.nameAr}</p>
                        <p className="text-xs text-gray-400">{p.price.toLocaleString('ar-SA')} ر.س</p>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md">{p.category}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Checking */}
          {status === 'checking' && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="w-12 h-12 border-2 border-gray-200 border-t-amber-500 rounded-full animate-spin"></div>
              <p className="text-gray-500 text-sm">جاري فحص قاعدة البيانات...</p>
            </div>
          )}

          {/* Ready — show selection */}
          {status === 'ready' && (
            <div className="flex flex-col gap-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-lg font-black text-[#1a1a1a]">{rows.length}</p>
                  <p className="text-xs text-gray-400 mt-0.5">إجمالي</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 text-center">
                  <p className="text-lg font-black text-green-600">{newCount}</p>
                  <p className="text-xs text-gray-400 mt-0.5">جديد</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 text-center">
                  <p className="text-lg font-black text-amber-600">{rows.length - newCount}</p>
                  <p className="text-xs text-gray-400 mt-0.5">موجود مسبقاً</p>
                </div>
              </div>

              {newCount === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 gap-3 bg-green-50 rounded-xl">
                  <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-checkbox-circle-line text-green-500 text-2xl"></i>
                  </div>
                  <p className="text-green-700 font-bold text-sm">جميع المنتجات موجودة في قاعدة البيانات!</p>
                  <p className="text-green-600/70 text-xs">لا يوجد شيء جديد للنقل</p>
                </div>
              ) : (
                <>
                  {/* Select all toggle */}
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-500">اختر المنتجات للنقل:</p>
                    <button
                      onClick={toggleAll}
                      className="text-xs text-[#1a1a1a] font-bold hover:underline cursor-pointer whitespace-nowrap"
                    >
                      {rows.filter(r => !r.exists).every(r => r.selected) ? 'إلغاء الكل' : 'تحديد الكل'}
                    </button>
                  </div>

                  <div className="flex flex-col gap-2">
                    {rows.map((row, idx) => (
                      <div
                        key={row.product.id}
                        onClick={() => !row.exists && toggleSelect(idx)}
                        className={`flex items-center gap-3 rounded-xl px-4 py-3 border transition-all ${
                          row.exists
                            ? 'bg-gray-50 border-gray-100 opacity-60 cursor-default'
                            : row.selected
                            ? 'bg-amber-50 border-amber-200 cursor-pointer'
                            : 'bg-white border-gray-100 cursor-pointer hover:border-gray-200'
                        }`}
                      >
                        {/* Checkbox */}
                        <div className={`w-5 h-5 flex items-center justify-center rounded-md border-2 flex-shrink-0 transition-all ${
                          row.exists
                            ? 'border-gray-200 bg-gray-100'
                            : row.selected
                            ? 'border-amber-500 bg-amber-500'
                            : 'border-gray-300'
                        }`}>
                          {(row.exists || row.selected) && (
                            <i className={`ri-check-line text-xs ${row.exists ? 'text-gray-400' : 'text-white'}`}></i>
                          )}
                        </div>

                        {/* Image */}
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          {row.product.images?.[0] && (
                            <img src={row.product.images[0]} alt={row.product.nameAr} className="w-full h-full object-cover object-top" />
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1a1a1a] truncate">{row.product.nameAr}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{row.product.price.toLocaleString('ar-SA')} ر.س</p>
                        </div>

                        {/* Status badge */}
                        {row.exists ? (
                          <span className="text-[10px] font-bold bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                            موجود
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                            جديد
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Importing */}
          {status === 'importing' && (
            <div className="flex flex-col items-center justify-center py-10 gap-5">
              <div className="relative w-20 h-20">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="#f3f4f6" strokeWidth="6" />
                  <circle
                    cx="40" cy="40" r="34" fill="none"
                    stroke="#f59e0b" strokeWidth="6"
                    strokeDasharray={`${2 * Math.PI * 34}`}
                    strokeDashoffset={`${2 * Math.PI * 34 * (1 - progress / 100)}`}
                    strokeLinecap="round"
                    className="transition-all duration-300"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-black text-[#1a1a1a]">{progress}%</span>
                </div>
              </div>
              <div className="text-center">
                <p className="font-bold text-[#1a1a1a] text-sm">جاري نقل المنتجات...</p>
                <p className="text-gray-400 text-xs mt-1">يرجى الانتظار</p>
              </div>
            </div>
          )}

          {/* Done */}
          {status === 'done' && (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <div className="w-20 h-20 flex items-center justify-center bg-green-50 rounded-2xl">
                <i className="ri-checkbox-circle-line text-green-500 text-5xl"></i>
              </div>
              <div className="text-center">
                <h3 className="font-black text-[#1a1a1a] text-lg">تم النقل بنجاح!</h3>
                <p className="text-gray-500 text-sm mt-1">
                  تم نقل <span className="font-black text-green-600">{importedCount}</span> منتج إلى قاعدة البيانات
                </p>
              </div>
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700">
                <i className="ri-cloud-line text-base"></i>
                <span className="font-medium">المنتجات محفوظة في Supabase الآن</span>
              </div>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-2xl">
                <i className="ri-error-warning-line text-amber-500 text-3xl"></i>
              </div>
              <div className="text-center">
                <h3 className="font-bold text-[#1a1a1a] text-base">اكتمل مع تحذيرات</h3>
                <p className="text-gray-500 text-sm mt-1">{errorMsg}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            {status === 'done' ? 'إغلاق' : 'إلغاء'}
          </button>

          {status === 'idle' && (
            <button
              onClick={handleCheck}
              className="bg-amber-500 hover:bg-amber-400 text-white rounded-xl px-6 py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-search-line"></i>
              فحص المنتجات
            </button>
          )}

          {status === 'ready' && newCount > 0 && selectedCount > 0 && (
            <button
              onClick={handleImport}
              className="bg-[#1a1a1a] hover:bg-[#333] text-white rounded-xl px-6 py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-upload-cloud-2-line"></i>
              نقل {selectedCount} منتج
            </button>
          )}

          {status === 'ready' && newCount === 0 && (
            <button
              onClick={onClose}
              className="bg-green-500 hover:bg-green-600 text-white rounded-xl px-6 py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-checkbox-circle-line"></i>
              ممتاز!
            </button>
          )}

          {(status === 'done' || status === 'error') && (
            <button
              onClick={onClose}
              className="bg-[#1a1a1a] hover:bg-[#333] text-white rounded-xl px-6 py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
            >
              عرض المنتجات
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
